import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Box,
  type SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  remediationService,
  type RemediationPlanSummary,
  type RemediationPriority,
} from '@services/remediationService';
import type { FindingSeverity } from '@services/auditService';

const NEW_PLAN = '__new__';

/** Finding severity maps onto the remediation item's priority band. */
const SEVERITY_TO_PRIORITY: Record<FindingSeverity, RemediationPriority> = {
  critical: 'critical',
  major: 'high',
  minor: 'normal',
  info: 'low',
};

export interface AddToRemediationPlanDialogProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  finding: {
    id: string;
    title: string;
    severity: FindingSeverity;
    contextId?: string | null;
  };
  /** Called after the finding is successfully added, with the plan's name. */
  onAdded: (planName: string) => void;
}

/**
 * REV-466: add a finding to a remediation plan. The backend already supports
 * linking a RemediationItem to a finding (finding_instance_id); this is the
 * missing UI that sets the link — picking an existing plan or creating one,
 * then creating an item carrying the finding id, its context, and a priority
 * derived from the finding's severity.
 */
export default function AddToRemediationPlanDialog({
  open,
  onClose,
  orgId,
  finding,
  onAdded,
}: AddToRemediationPlanDialogProps) {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<RemediationPlanSummary[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [newPlanName, setNewPlanName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setSelectedPlanId('');
    setNewPlanName('');
    setLoadingPlans(true);
    void (async () => {
      try {
        const list = await remediationService.listPlans(orgId);
        if (cancelled) return;
        setPlans(list);
        // Default to the first existing plan, else prompt to create one.
        const first = list[0];
        setSelectedPlanId(first ? first.id : NEW_PLAN);
      } catch {
        if (!cancelled)
          setError(t('remediation.addToPlan.loadFailed', 'Could not load your plans.'));
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, orgId, t]);

  const handlePlanChange = (e: SelectChangeEvent) => setSelectedPlanId(e.target.value);

  const handleSubmit = async () => {
    setError(null);
    const creatingNew = selectedPlanId === NEW_PLAN;
    if (!selectedPlanId) {
      setError(t('remediation.addToPlan.pickPlan', 'Choose a plan or create a new one.'));
      return;
    }
    if (creatingNew && !newPlanName.trim()) {
      setError(t('remediation.addToPlan.nameRequired', 'Enter a name for the new plan.'));
      return;
    }
    setSaving(true);
    try {
      let planId = selectedPlanId;
      let planName: string;
      if (creatingNew) {
        const created = await remediationService.createPlan(orgId, { name: newPlanName.trim() });
        planId = created.id;
        planName = created.name;
      } else {
        planName = plans.find((p) => p.id === planId)?.name ?? '';
      }
      await remediationService.createItem(orgId, planId, {
        title: finding.title,
        finding_instance_id: finding.id,
        context_id: finding.contextId ?? null,
        priority: SEVERITY_TO_PRIORITY[finding.severity],
      });
      onAdded(planName);
      onClose();
    } catch {
      setError(
        t('remediation.addToPlan.saveFailed', 'Could not add to the plan. Please try again.')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('remediation.addToPlan.title', 'Add to remediation plan')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loadingPlans ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} aria-label={t('common.loading', 'Loading...')} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="rem-plan-label">{t('remediation.addToPlan.plan', 'Plan')}</InputLabel>
              <Select
                labelId="rem-plan-label"
                value={selectedPlanId}
                label={t('remediation.addToPlan.plan', 'Plan')}
                onChange={handlePlanChange}
              >
                {plans.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
                <MenuItem value={NEW_PLAN}>
                  {t('remediation.addToPlan.createNew', '+ Create a new plan')}
                </MenuItem>
              </Select>
            </FormControl>
            {selectedPlanId === NEW_PLAN && (
              <TextField
                size="small"
                fullWidth
                label={t('remediation.addToPlan.newPlanName', 'New plan name')}
                value={newPlanName}
                onChange={(e) => setNewPlanName(e.target.value)}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? (
            <CircularProgress size={20} aria-label={t('common.saving', 'Saving…')} />
          ) : (
            t('remediation.addToPlan.submit', 'Add')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
